import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, List, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import SelectorAviso from '../../components/SelectorAviso';
import { money, moneyK, fecha, Title, Badge, Empty, Kpi } from './ui';
import { ESTADO_PROYECTO } from './Proyecto';

// El orden de las columnas es el recorrido real de un trabajo
const ETAPAS = ['planificacion', 'en_desarrollo', 'revision', 'publicacion', 'terminado'];

const diasDesde = (f) => (f ? Math.round((Date.now() - new Date(f)) / 86400000) : null);

export default function AgenciaProyectos() {
  const [rows, setRows] = useState(null);
  const [vista, setVista] = useState('tablero');
  const [arrastrando, setArrastrando] = useState(null);
  const [sobre, setSobre] = useState(null);
  const [aviso, setAviso] = useState(null);      // preguntar si avisamos después de mover
  const [verCancelados, setVerCancelados] = useState(false);
  const navigate = useNavigate();

  const cargar = useCallback(() => {
    api.get('/agency/proyectos').then(r => setRows(r.data)).catch(() => toast.error('No se pudo cargar'));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  if (!rows) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const mover = async (p, estado) => {
    if (!p || p.estado === estado) return;
    setRows(rows.map(r => (r.id === p.id ? { ...r, estado } : r)));   // se ve al instante
    try {
      await api.put('/agency/proyectos/' + p.id, { estado, notificar: 'no' });
      setAviso({ proyecto: { ...p, estado }, canal: 'portal', mensaje: '' });
    } catch { toast.error('No se pudo mover'); cargar(); }
  };

  const avisar = async () => {
    try {
      // se vuelve a guardar el mismo estado, ahora sí con aviso
      await api.put('/agency/proyectos/' + aviso.proyecto.id, { estado: aviso.proyecto.estado, notificar: aviso.canal, mensaje: aviso.mensaje });
      toast.success(aviso.canal === 'email' ? 'Le avisamos por email' : aviso.canal === 'portal' ? 'Le queda el aviso en el portal' : 'Le avisamos por portal y email');
      setAviso(null);
    } catch { toast.error('No se pudo avisar'); }
  };

  const activos = rows.filter(r => !['terminado', 'cancelado'].includes(r.estado));
  const enCurso = activos.reduce((a, r) => a + Number(r.valor || 0), 0);
  const porCobrar = rows.reduce((a, r) => a + Number(r.por_cobrar || 0), 0);
  const mes = new Date().toISOString().slice(0, 7);
  const terminadosMes = rows.filter(r => r.estado === 'terminado' && String(r.fecha_fin || '').slice(0, 7) === mes).length;

  const visibles = verCancelados ? rows : rows.filter(r => r.estado !== 'cancelado');
  const th = { textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, padding: '8px' };
  const td = { padding: '10px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };
  const btn = (a) => ({ border: '1px solid ' + (a ? 'var(--accent)' : 'var(--border)'), background: a ? 'var(--accent)' : 'transparent', color: a ? '#fff' : 'var(--text-secondary)' });

  const Tarjeta = ({ p }) => {
    const dias = diasDesde(p.ultimo_movimiento);
    return (
      <div draggable
        onDragStart={ev => {
          ev.dataTransfer.effectAllowed = 'move';
          ev.dataTransfer.setData('text/plain', String(p.id));   // sin esto, varios navegadores no arrancan el arrastre
          setArrastrando(p);
        }}
        onDragEnd={() => { setArrastrando(null); setSobre(null); }}
        onClick={ev => { if (!ev.defaultPrevented) navigate('/agencia/proyectos/' + p.id); }}
        style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 12, marginBottom: 10,
          cursor: 'grab', opacity: arrastrando && arrastrando.id === p.id ? 0.4 : 1, boxShadow: 'var(--shadow-sm, none)',
        }}>
        <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.3 }}>{p.nombre}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{p.cliente}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {p.valor ? <span style={{ fontSize: 12, fontWeight: 700 }}>{moneyK(p.valor)}</span> : <span />}
          {Number(p.por_cobrar) > 0 && <span style={{ fontSize: 11, color: '#f59e0b' }}>{moneyK(p.por_cobrar)} por cobrar</span>}
        </div>
        {dias !== null && dias > 21 && !['terminado', 'cancelado'].includes(p.estado) && (
          <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 6 }}>sin novedades hace {dias} días</div>
        )}
        {dias === null && !['terminado', 'cancelado'].includes(p.estado) && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>sin movimientos cargados</div>
        )}
      </div>
    );
  };

  return (
    <div>
      <Title right={
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" style={btn(vista === 'tablero')} onClick={() => setVista('tablero')}><LayoutGrid size={15} /> Tablero</button>
          <button className="btn btn-sm" style={btn(vista === 'lista')} onClick={() => setVista('lista')}><List size={15} /> Lista</button>
        </div>
      }>Proyectos</Title>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        <Kpi label="En curso" value={activos.length} sub={enCurso ? moneyK(enCurso) : null} color="var(--accent)" />
        <Kpi label="Terminados este mes" value={terminadosMes} color="#22c55e" />
        <Kpi label="Por cobrar" value={moneyK(porCobrar)} sub="de todos los proyectos" color="#f59e0b" />
        <Kpi label="Sin novedades hace 3+ semanas" value={activos.filter(r => (diasDesde(r.ultimo_movimiento) ?? 999) > 21).length} color="#ef4444" />
      </div>

      {vista === 'tablero' ? (
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 10, alignItems: 'flex-start' }}>
          {ETAPAS.map(etapa => {
            const e = ESTADO_PROYECTO[etapa];
            const enEtapa = visibles.filter(p => p.estado === etapa);
            return (
              <div key={etapa}
                onDragOver={ev => { ev.preventDefault(); ev.dataTransfer.dropEffect = 'move'; if (sobre !== etapa) setSobre(etapa); }}
                onDragLeave={ev => { if (!ev.currentTarget.contains(ev.relatedTarget)) setSobre(s => (s === etapa ? null : s)); }}
                onDrop={ev => {
                  ev.preventDefault();
                  const id = Number(ev.dataTransfer.getData('text/plain'));
                  const p = arrastrando || rows.find(r => r.id === id);
                  mover(p, etapa);
                  setArrastrando(null); setSobre(null);
                }}
                style={{
                  flex: '1 0 230px', minWidth: 230, minHeight: 220, background: sobre === etapa ? 'var(--accent-glow)' : 'var(--bg-secondary)',
                  border: '1px solid ' + (sobre === etapa ? 'var(--accent)' : 'var(--border)'), borderRadius: 14, padding: 12,
                  transition: 'background 0.15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.color }} />
                  <span style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, color: 'var(--text-secondary)' }}>{e.label}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>{enEtapa.length}</span>
                </div>
                {enEtapa.map(p => <Tarjeta key={p.id} p={p} />)}
                {!enEtapa.length && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '20px 0', textAlign: 'center',
                    border: '1px dashed var(--border)', borderRadius: 10 }}>Soltá un proyecto acá</div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-container">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Proyecto</th><th style={th}>Cliente</th><th style={th}>Estado</th>
                <th style={th}>Inicio</th><th style={th}>Último movimiento</th>
                <th style={{ ...th, textAlign: 'right' }}>Valor</th><th style={{ ...th, textAlign: 'right' }}>Por cobrar</th>
              </tr></thead>
              <tbody>
                {visibles.length ? visibles.map(p => {
                  const dias = diasDesde(p.ultimo_movimiento);
                  const e = ESTADO_PROYECTO[p.estado] || {};
                  return (
                    <tr key={p.id}>
                      <td style={{ ...td, fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/agencia/proyectos/' + p.id)}>{p.nombre}</td>
                      <td style={{ ...td, cursor: 'pointer' }} onClick={() => navigate('/agencia/clientes/' + p.cliente_id)}>{p.cliente}</td>
                      <td style={td}>
                        <select value={p.estado} onChange={ev => mover(p, ev.target.value)}
                          style={{ width: 'auto', padding: '4px 24px 4px 9px', fontSize: 12, fontWeight: 700, borderRadius: 999,
                            background: e.bg || 'transparent', color: e.color, border: '1px solid var(--border)' }}>
                          {Object.entries(ESTADO_PROYECTO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>{fecha(p.fecha_inicio)}</td>
                      <td style={{ ...td, fontSize: 12, color: dias > 21 ? '#f59e0b' : 'var(--text-muted)' }}>
                        {dias === null ? 'sin movimientos' : dias === 0 ? 'hoy' : `hace ${dias} días`}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{p.valor ? money(p.valor) : '—'}</td>
                      <td style={{ ...td, textAlign: 'right', color: Number(p.por_cobrar) ? '#f59e0b' : 'var(--text-muted)' }}>
                        {Number(p.por_cobrar) ? money(p.por_cobrar) : '—'}</td>
                    </tr>
                  );
                }) : <tr><td colSpan={7} style={td}><Empty>Todavía no hay proyectos. Se crean al cerrar una propuesta o desde la ficha del cliente.</Empty></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
          <input type="checkbox" checked={verCancelados} onChange={e => setVerCancelados(e.target.checked)} style={{ width: 15, height: 15 }} />
          Ver también los cancelados
        </label>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
          <Plus size={12} /> Los proyectos se crean al cerrar una propuesta o desde la ficha del cliente
        </span>
      </div>

      {aviso && (
        <div className="modal-overlay" onClick={() => setAviso(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <h2>Pasó a {(ESTADO_PROYECTO[aviso.proyecto.estado] || {}).label}</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
              El cambio ya quedó guardado. ¿Querés que el cliente se entere?
            </p>
            <SelectorAviso value={aviso.canal} onChange={c => setAviso({ ...aviso, canal: c })} />
            {aviso.canal !== 'no' && (
              <div className="form-group"><label>Mensaje (opcional)</label>
                <textarea rows={3} value={aviso.mensaje} onChange={e => setAviso({ ...aviso, mensaje: e.target.value })}
                  style={{ fontFamily: 'inherit', fontSize: 13 }} placeholder="Ya está la primera versión para que la revises." /></div>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setAviso(null)}>No avisar</button>
              <button className="btn btn-primary" onClick={avisar} disabled={aviso.canal === 'no'}>Avisar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
